import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { captureReferralSlug, track } from "@/lib/analytics";
import { trackAffiliateVisit } from "@/lib/affiliate";
import { studyFunnelSignal } from "@/lib/study-conversion";

/**
 * Public entry point for referral links (/signup?ref=CODE). The code is captured
 * for later server-side attribution, then the visitor continues to the normal
 * sign-in / sign-up screen — no separate auth implementation.
 */
export const Route = createFileRoute("/signup")({
  component: SignupRedirect,
  head: () => ({
    meta: [
      { title: "Create your Inquira account — AI study help" },
      {
        name: "description",
        content: "Sign up for Inquira to scan any question and get step-by-step AI explanations, flashcards, quizzes and practice.",
      },
      { property: "og:title", content: "Create your Inquira account" },
      { property: "og:description", content: "Join Inquira for step-by-step AI explanations of any question." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/signup" }],
  }),
});

function SignupRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    captureReferralSlug();
    try { window.sessionStorage.setItem("inquira.signup.mode", "free"); } catch { /* optional navigation hint */ }
    studyFunnelSignal("registration_started");
    void trackAffiliateVisit();
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      // Same storage key the sign-in screen reads, so attribution is applied
      // server-side (apply_referral_code) right after the account is created.
      try { window.localStorage.setItem("inquira_ref", ref.toUpperCase()); } catch { /* ignore */ }
      void track("referral_signup", { has_ref: true });
    }
    navigate({ to: "/login", replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <p className="text-sm text-ink-muted">Taking you to sign up…</p>
    </div>
  );
}
